import { ExternalLink } from "@/components/ExternalLink";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { formatNumber, parseDate, parseDateWithHoursUTC } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { getAuthorized } from "@/lib/fetch";
import { useTranslation } from "react-i18next";

const client = "l1";
const table_name = "l1_events";
const static_data_table_name = "l1_static_data";

export type L1EventRow = {
  id: number;
  l1_static_datum_id: number;
  l1_static_datum_name?: string | null;
  date?: string;
  event?: string;
  ratio?: number | null;
  created_at?: string;
  updated_at?: string;
};

export function useL1EventColumns() {
  const { t, i18n } = useTranslation("tables");

  const language = i18n.resolvedLanguage ?? "fr";
  const { data: translations } = useQuery({
    queryKey: [client, table_name, "translations", language],
    queryFn: () =>
      getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/${client}/${table_name}/translations?language=${language}`
      ).then((r) => r.json()),
  });

  const getEventTranslation = (event: string | null | undefined): string => {
    if (!event) return "";
    return ((translations?.event as Record<string, string> | undefined)?.[event]) ?? event;
  };

  const columns: ColumnDef<L1EventRow>[] = [
    {
      accessorKey: "id",
      header: t(`${table_name}.columns.id`, { defaultValue: "ID" }),
    },
    {
      accessorKey: "l1_static_datum_name",
      header: t(`${table_name}.columns.l1_static_datum_name`, {
        defaultValue: "Asset",
      }),
      cell: ({ row }) => (
        <ExternalLink
          href={toWorkspacePath(
            `/admin/${client}/${static_data_table_name}/${row.original.l1_static_datum_id}`,
          )}
          className="text-xs whitespace-nowrap"
          underline="hover"
        >
          {String(row.getValue("l1_static_datum_name") ?? "")}
        </ExternalLink>
      ),
    },
    {
      accessorKey: "date",
      header: t(`${table_name}.columns.date`, { defaultValue: "Date" }),
      cell: ({ row }) => parseDate(String(row.getValue("date") ?? "")),
    },
    {
      accessorKey: "event",
      header: t(`${table_name}.columns.event`, { defaultValue: "Event type" }),
      cell: ({ row }) =>
        getEventTranslation(row.getValue("event")),
    },
    {
      accessorKey: "ratio",
      header: () => (
        <div className="text-right">
          {t(`${table_name}.columns.ratio`, {
            defaultValue: "Ratio (before:after)",
          })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">{formatNumber(row.getValue("ratio"))}</div>
      ),
    },
    {
      accessorKey: "created_at",
      header: t(`${table_name}.columns.created_at`, {
        defaultValue: "Created at",
      }),
      cell: ({ row }) =>
        parseDateWithHoursUTC(row.getValue("created_at") as string | undefined),
    },
    {
      accessorKey: "updated_at",
      header: t(`${table_name}.columns.updated_at`, {
        defaultValue: "Updated at",
      }),
      cell: ({ row }) =>
        parseDateWithHoursUTC(row.getValue("updated_at") as string | undefined),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue("id")}`}
            className="text-xs"
            underline="hover"
          >
            {t("actions.edit", { ns: "translation", defaultValue: "Edit" })}
          </Link>
        </div>
      ),
    },
  ];

  return columns;
}
